import { AspectRatio, Button, Card, CardContent, CardHeader, CardTitle } from "@repo/ui";

const IMAGES = [
  {
    src: "https://images.pexels.com/photos/6692600/pexels-photo-6692600.jpeg",
    alt: "A collection of handcrafted ceramic bowls and cups in a minimalist style on a wooden table.",
  },
  {
    src: "https://images.pexels.com/photos/8063822/pexels-photo-8063822.jpeg",
    alt: "A still life arrangement of glazed ceramic vases showcasing artistic craftsmanship.",
  },
  {
    src: "https://images.pexels.com/photos/8063848/pexels-photo-8063848.jpeg",
    alt: "Close-up view of clay vases on a shelf, showcasing minimalist pottery design against a neutral background.",
  },
  {
    src: "https://images.pexels.com/photos/5377301/pexels-photo-5377301.jpeg",
    alt: "Close-up of rustic clay cups arranged on a concrete surface, highlighting their craftsmanship.",
  },
  {
    src: "https://images.pexels.com/photos/7260337/pexels-photo-7260337.jpeg",
    alt: "Artistic display of minimalist earthenware bowls and mugs in studio lighting.",
  },
  {
    src: "https://images.pexels.com/photos/6694768/pexels-photo-6694768.jpeg",
    alt: "Close-up of a woman shaping clay, highlighting handmade pottery craftsmanship.",
  },
];

export function Home() {
  return (
    <div className="min-h-screen">
      {/* Header with serif wordmark */}
      <header className="border-b border-border/80 bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/70 sticky top-0 z-40">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <a
            href="/"
            className="text-2xl font-semibold font-serif tracking-tight text-foreground"
            aria-label="Mano Studio home"
          >
            mano studio
          </a>
          <Button asChild size="lg">
            <a href="#shop">Shop ceramics</a>
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section
        className="w-full border-b"
        style={{
          background: "linear-gradient(180deg, var(--background) 0%, var(--secondary) 100%)",
        }}
      >
        <div className="container mx-auto px-6 py-16 md:py-24 grid md:grid-cols-2 gap-10 items-center">
          <div className="space-y-6">
            <h1 className="text-4xl md:text-6xl font-serif tracking-tight text-foreground">
              Timeless ceramics, hand-thrown in Bangkok
            </h1>
            <p className="text-lg text-muted-foreground max-w-prose">
              Small-batch vessels focused on texture, balance, and quiet warmth. Designed for
              everyday rituals.
            </p>
            <div className="flex gap-4">
              <Button size="lg" asChild>
                <a href="#shop">Shop ceramics</a>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="#location">Visit the studio</a>
              </Button>
            </div>
          </div>
          <div>
            <Card className="overflow-hidden shadow-none">
              <CardContent className="p-0">
                <AspectRatio ratio={4 / 5}>
                  <img
                    src={IMAGES[1].src}
                    alt={IMAGES[1].alt}
                    className="h-full w-full object-cover"
                    loading="eager"
                  />
                </AspectRatio>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Featured ceramics */}
      <section id="shop" className="container mx-auto px-6 py-16 md:py-24 space-y-8">
        <div className="flex items-end justify-between">
          <div>
            <h2 className="text-2xl md:text-3xl font-serif tracking-tight">Featured pieces</h2>
            <p className="text-muted-foreground">Select works available in our shop.</p>
          </div>
          <Button asChild variant="link" className="px-0">
            <a href="#shop">View all →</a>
          </Button>
        </div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {IMAGES.slice(0, 6).map((img, i) => (
            <Card key={i} className="overflow-hidden">
              <CardContent className="p-0">
                <AspectRatio ratio={4 / 5}>
                  <img
                    src={img.src}
                    alt={img.alt}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </AspectRatio>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="pt-2">
          <Button size="lg" asChild>
            {/* Replace with external shop URL or /shop route when provided */}
            <a href="#shop">Shop ceramics</a>
          </Button>
        </div>
      </section>

      {/* About */}
      <section className="container mx-auto px-6 py-16 md:py-24">
        <div className="grid md:grid-cols-3 gap-10 items-start">
          <div className="md:col-span-2 space-y-4">
            <h3 className="text-xl md:text-2xl font-serif tracking-tight">About Mano Studio</h3>
            <p className="text-muted-foreground max-w-prose">
              Mano Studio is a small pottery studio based in Bangkok, Thailand. Our work draws from
              Scandinavian minimalism and natural materials—favoring matte glazes, grounded
              silhouettes, and a warm neutral palette.
            </p>
          </div>
          <Card className="bg-secondary">
            <CardHeader>
              <CardTitle className="text-base">Details</CardTitle>
            </CardHeader>
            <CardContent className="text-sm text-muted-foreground space-y-2">
              <p>Small-batch releases</p>
              <p>Hand-thrown stoneware</p>
              <p>Bangkok, Thailand</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Location */}
      <section id="location" className="border-t border-border/80 bg-secondary/40">
        <div className="container mx-auto px-6 py-16 md:py-24 space-y-6">
          <h3 className="text-xl md:text-2xl font-serif tracking-tight">Visit the studio</h3>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-3 text-muted-foreground">
              <p>Bangkok, Thailand</p>
              <p className="text-sm">Exact address to be confirmed.</p>
              <Button asChild variant="outline">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Bangkok%2C%20Thailand"
                  target="_blank"
                  rel="noreferrer"
                >
                  Open in Google Maps
                </a>
              </Button>
            </div>
            <div className="rounded-md overflow-hidden border">
              <AspectRatio ratio={16 / 9}>
                <iframe
                  title="Mano Studio on Google Maps"
                  src="https://maps.google.com/maps?q=Bangkok%2C%20Thailand&t=&z=12&ie=UTF8&iwloc=&output=embed"
                  loading="lazy"
                  className="h-full w-full"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </AspectRatio>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t">
        <div className="container mx-auto px-6 py-8 text-sm text-muted-foreground flex items-center justify-between">
          <span>© {new Date().getFullYear()} Mano Studio</span>
          <a className="hover:underline" href="#shop">
            Shop ceramics
          </a>
        </div>
      </footer>
    </div>
  );
}
